import type { ComparisonConfig } from './ComparisonPage';
import type { LocaleContentMap } from '@/core/i18n/content';
import { COMPARISON_CONFIGS_P2 } from './seo-comparison-p2.data';
import { COMPARISON_CONFIGS_P3 } from './seo-comparison-p3.data';
import { COMPARISON_CONFIGS_P4 } from './seo-comparison-p4.data';
import { getP1Locales, getP2Locales, getP3Locales, getP4Locales, injectAI, injectAILocaleMap } from './seo-comparison-locales';
export { BEST_SOFTWARE_CONFIG, BEST_SOFTWARE_FAQ } from './seo-best-software.data';

/* ========================================================================== */
/*  HotelStack.io vs Hotelogix                                                */
/* ========================================================================== */

const HOTELSTACK_VS_HOTELOGIX: ComparisonConfig = {
  slug: 'hotelstack-vs-hotelogix',
  competitorName: 'Hotelogix',
  heroTitle: 'HotelStack.io vs Hotelogix',
  heroSubtitle: 'A comprehensive comparison of two leading hotel management platforms. See how HotelStack.io delivers more features, modern architecture, and better value than Hotelogix.',
  overviewComparison: [
    { label: 'Total Modules', hotelstack: '36 Integrated Modules', competitor: '15 Modules' },
    { label: 'Architecture', hotelstack: 'Cloud-Native SaaS', competitor: 'Legacy Cloud' },
    { label: 'Global Reach', hotelstack: '40+ Countries', competitor: 'Primarily Asia' },
    { label: 'Channel Manager', hotelstack: 'Built-In (100+ OTAs)', competitor: 'Add-On Required' },
    { label: 'Booking Engine', hotelstack: 'BookMyRoom.io (Free)', competitor: 'Basic Widget' },
    { label: 'Support', hotelstack: '24/7 Dedicated', competitor: 'Email + Chat' },
  ],
  featureTable: [
    {
      category: 'Core PMS',
      features: [
        { name: 'Visual Room Board', hotelstack: true, competitor: true },
        { name: 'Drag-and-Drop Check-In', hotelstack: true, competitor: false },
        { name: 'Night Audit Wizard', hotelstack: true, competitor: true },
        { name: 'Guest Folio Management', hotelstack: true, competitor: true },
        { name: 'Online Check-In Portal', hotelstack: true, competitor: false },
        { name: 'Walk-In Booking Modal', hotelstack: true, competitor: 'Limited' },
      ],
    },
    {
      category: 'Revenue Management',
      features: [
        { name: 'Dynamic Pricing Rules', hotelstack: true, competitor: false },
        { name: 'What-If Revenue Simulator', hotelstack: true, competitor: false },
        { name: 'Competitor Rate Monitor', hotelstack: true, competitor: false },
        { name: 'Demand Forecasting', hotelstack: true, competitor: false },
        { name: 'RevPAR Analytics', hotelstack: true, competitor: 'Basic' },
      ],
    },
    {
      category: 'Guest Management',
      features: [
        { name: '360-Degree Guest Profiles', hotelstack: true, competitor: 'Basic' },
        { name: 'Loyalty Program Management', hotelstack: true, competitor: false },
        { name: 'Guest Communication Automation', hotelstack: true, competitor: 'Limited' },
        { name: 'Guest Spend Analytics', hotelstack: true, competitor: false },
        { name: 'ID Upload & Photo Capture', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'F&B & POS',
      features: [
        { name: 'POS Terminal', hotelstack: true, competitor: false },
        { name: 'Table Floor Plan', hotelstack: true, competitor: false },
        { name: 'Kitchen Display System', hotelstack: true, competitor: false },
        { name: 'Room Service Module', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Reporting & Analytics',
      features: [
        { name: 'Flash Report', hotelstack: true, competitor: true },
        { name: 'P&L Statement', hotelstack: true, competitor: false },
        { name: 'Pace Report', hotelstack: true, competitor: false },
        { name: 'Tax Compliance Reports', hotelstack: true, competitor: 'Basic' },
        { name: 'Custom Report Builder', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'AI & Intelligence',
      features: [
        { name: 'AI Dynamic Pricing', hotelstack: true, competitor: false },
        { name: 'AI Demand Forecasting (30/60/90 day)', hotelstack: true, competitor: false },
        { name: 'AI Review Response Generator', hotelstack: true, competitor: false },
        { name: 'AI Guest Concierge (Multilingual)', hotelstack: true, competitor: false },
        { name: 'AI Sentiment Analysis', hotelstack: true, competitor: false },
        { name: 'Predictive Maintenance', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Multi-Property',
      features: [
        { name: 'Chain Dashboard', hotelstack: true, competitor: true },
        { name: 'Cross-Property Analytics', hotelstack: true, competitor: false },
        { name: 'Centralized Rate Management', hotelstack: true, competitor: false },
        { name: 'Brand Settings', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Resilience & Offline',
      features: [
        { name: 'Offline-Capable PWA', hotelstack: true, competitor: false },
        { name: 'Offline Mutation Queue', hotelstack: true, competitor: false },
        { name: 'Background Sync on Reconnect', hotelstack: true, competitor: false },
        { name: 'Conflict-Aware Reconciliation', hotelstack: true, competitor: false },
        { name: 'Installable Front Desk', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Corporate & Group Sales',
      features: [
        { name: 'Corporate Rate Management', hotelstack: true, competitor: false },
        { name: 'Group Booking with Rooming Lists', hotelstack: true, competitor: 'Add-On' },
        { name: 'AR Aging & Production Reports', hotelstack: true, competitor: false },
        { name: 'Dining Reservations', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Staff & HR',
      features: [
        { name: 'Staff Directory & Scheduling', hotelstack: true, competitor: false },
        { name: 'Payroll Processing', hotelstack: true, competitor: false },
        { name: 'Leave & Attendance', hotelstack: true, competitor: false },
        { name: 'Performance Analytics', hotelstack: true, competitor: false },
      ],
    },
  ],
  pricingComparison: {
    hotelstackStarting: '$49',
    competitorStarting: '$60+',
    hotelstackNote: 'All 36 modules included. No feature restrictions. Free onboarding and data migration. Regional pricing in EUR, GBP, AED, INR, THB.',
    competitorNote: 'Base plan with limited modules. Channel manager and advanced features cost extra.',
  },
  advantages: [
    'More than double the modules (36 vs 15) with no add-on fees for core features',
    'Modern cloud-native architecture built in 2024 with real-time sync across all devices',
    'Multi-currency support with regional pricing in 6 currencies and compliance tools for 40+ countries',
    'Built-in booking engine via BookMyRoom.io — commission-free direct bookings included',
    'Advanced revenue management with dynamic pricing, demand forecasting, and what-if simulators',
    'Complete F&B and POS module with table management, kitchen display, and room service',
    'Lower starting price with more features included in the base plan',
    'Free data migration from Hotelogix with dedicated onboarding support',
  ],
  verdict: {
    title: 'Our Verdict',
    summary: 'HotelStack.io is the stronger choice for hotels seeking a modern, all-in-one platform. With 36 modules, built-in revenue management, F&B capabilities, and lower pricing, it delivers significantly more value than Hotelogix. Hotelogix may suit budget hotels needing only basic PMS functionality.',
    bestFor: [
      'Hotels wanting a modern all-in-one platform without add-on fees',
      'Properties needing advanced revenue management and dynamic pricing',
      'Hotels requiring F&B, POS, and room service capabilities',
      'Properties needing regional compliance and multi-currency billing',
      'Multi-property chains needing centralized analytics and rate management',
    ],
  },
  faqItems: [
    { question: 'Can I migrate from Hotelogix to HotelStack.io?', answer: 'Yes. Our team handles complete data migration from Hotelogix including guest profiles, reservation history, rate plans, and room configurations. Migration is free and typically completed within 48 hours with zero downtime.' },
    { question: 'Does HotelStack.io have all the features Hotelogix offers?', answer: 'HotelStack.io includes every feature Hotelogix offers plus 21 additional modules including revenue management, F&B/POS, loyalty programs, compliance tools, advanced analytics, HR/payroll, and corporate sales that Hotelogix does not provide.' },
    { question: 'How does pricing compare between HotelStack.io and Hotelogix?', answer: 'HotelStack.io starts at $49/month with all 36 modules included. Regional pricing available in EUR, GBP, AED, INR, and THB. Hotelogix starts at approximately $60/month for their base plan, with additional charges for channel manager and other features.' },
    { question: 'Is HotelStack.io suitable for small hotels currently using Hotelogix?', answer: 'Absolutely. Our Starter plan is designed for properties up to 50 rooms with all features unlocked. Many small hotels switch from Hotelogix for the additional modules and lower price point.' },
  ],
};

/* ========================================================================== */
/*  HotelStack.io vs Cloudbeds                                                */
/* ========================================================================== */

const HOTELSTACK_VS_CLOUDBEDS: ComparisonConfig = {
  slug: 'hotelstack-vs-cloudbeds',
  competitorName: 'Cloudbeds',
  heroTitle: 'HotelStack.io vs Cloudbeds',
  heroSubtitle: 'Both are cloud-native platforms. HotelStack.io offers more modules, lower pricing, and complete F&B/POS capabilities that Cloudbeds lacks — all with multi-currency regional pricing.',
  overviewComparison: [
    { label: 'Total Modules', hotelstack: '36 Modules', competitor: '20+ Modules' },
    { label: 'Pricing', hotelstack: 'From $49/mo', competitor: 'From $100+/mo' },
    { label: 'Regional Pricing', hotelstack: '6 Currencies', competitor: 'USD Only' },
    { label: 'F&B / POS', hotelstack: 'Built-In', competitor: 'Not Available' },
    { label: 'Booking Engine', hotelstack: 'BookMyRoom.io (Free)', competitor: 'Basic Widget' },
    { label: 'Onboarding', hotelstack: 'Free + Dedicated', competitor: 'Self-Service' },
  ],
  featureTable: [
    {
      category: 'Core PMS',
      features: [
        { name: 'Visual Room Board', hotelstack: true, competitor: true },
        { name: 'Reservation Management', hotelstack: true, competitor: true },
        { name: 'Group Bookings', hotelstack: true, competitor: true },
        { name: 'Night Audit Wizard', hotelstack: true, competitor: true },
        { name: 'Online Check-In Portal', hotelstack: true, competitor: true },
      ],
    },
    {
      category: 'Regional Compliance',
      features: [
        { name: 'Multi-Currency Billing', hotelstack: true, competitor: 'Limited' },
        { name: 'Regional Tax Automation', hotelstack: true, competitor: false },
        { name: 'Guest Registration Compliance', hotelstack: true, competitor: false },
        { name: 'GDPR Data Protection', hotelstack: true, competitor: true },
        { name: 'Regional Payment Gateways', hotelstack: true, competitor: 'Limited' },
        { name: 'Localized Invoicing', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Revenue Management',
      features: [
        { name: 'Dynamic Pricing', hotelstack: true, competitor: true },
        { name: 'Revenue Analytics', hotelstack: true, competitor: true },
        { name: 'Competitor Rate Monitor', hotelstack: true, competitor: 'Add-On' },
        { name: 'What-If Simulator', hotelstack: true, competitor: false },
        { name: 'Demand Forecasting', hotelstack: true, competitor: 'Basic' },
      ],
    },
    {
      category: 'F&B & Operations',
      features: [
        { name: 'POS Terminal', hotelstack: true, competitor: false },
        { name: 'Room Service Module', hotelstack: true, competitor: false },
        { name: 'Housekeeping Board', hotelstack: true, competitor: true },
        { name: 'Maintenance Work Orders', hotelstack: true, competitor: false },
        { name: 'Staff Scheduling', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Compliance & Reporting',
      features: [
        { name: 'Regional Tax Reports', hotelstack: true, competitor: false },
        { name: 'Audit Trail', hotelstack: true, competitor: true },
        { name: 'P&L Statement', hotelstack: true, competitor: 'Limited' },
        { name: 'Custom Report Builder', hotelstack: true, competitor: true },
      ],
    },
    {
      category: 'Resilience & Offline',
      features: [
        { name: 'Offline-Capable PWA', hotelstack: true, competitor: false },
        { name: 'Offline Mutation Queue', hotelstack: true, competitor: false },
        { name: 'Background Sync on Reconnect', hotelstack: true, competitor: false },
        { name: 'Conflict-Aware Reconciliation', hotelstack: true, competitor: false },
        { name: 'Installable Front Desk', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Corporate & Group Sales',
      features: [
        { name: 'Corporate Rate Management', hotelstack: true, competitor: false },
        { name: 'Group Booking with Rooming Lists', hotelstack: true, competitor: 'Limited' },
        { name: 'AR Aging & Production Reports', hotelstack: true, competitor: false },
        { name: 'Dining Reservations', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Staff & HR',
      features: [
        { name: 'Staff Directory & Scheduling', hotelstack: true, competitor: 'Add-On' },
        { name: 'Payroll Processing', hotelstack: true, competitor: false },
        { name: 'Leave & Attendance', hotelstack: true, competitor: false },
        { name: 'Performance Analytics', hotelstack: true, competitor: false },
      ],
    },
  ],
  pricingComparison: {
    hotelstackStarting: '$49',
    competitorStarting: '$100+',
    hotelstackNote: 'All 36 modules included. Regional pricing in EUR, GBP, AED, INR, THB. Free onboarding and migration.',
    competitorNote: 'USD-only pricing ($100+/mo). Additional charges for premium features and integrations.',
  },
  advantages: [
    'All 36 modules included at roughly half the cost — no add-on fees for core features',
    'Regional pricing in 6 currencies — no exchange rate surprises for international properties',
    'Complete F&B module with POS terminal, room service, and kitchen management',
    'Regional compliance tools for tax automation, guest registration, and GDPR',
    'Multi-gateway payment support via PaymentHooks.com (Stripe, PayPal, 50+ gateways) for global and local methods',
    'Staff management module with payroll, attendance, and shift scheduling',
    'Maintenance and work order management with asset tracking',
    'Free onboarding and data migration with dedicated support team',
  ],
  verdict: {
    title: 'Our Verdict',
    summary: 'HotelStack.io delivers more modules, better value, and complete operational tools at roughly half the price of Cloudbeds. While Cloudbeds is a solid platform for basic PMS and channel management, HotelStack.io adds F&B/POS, staff management, maintenance, advanced revenue tools, corporate sales, and HR — all included in the base price.',
    bestFor: [
      'Hotels wanting a complete all-in-one platform at a lower price point',
      'Properties needing F&B, POS, and staff management in one system',
      'Hotels wanting regional compliance and multi-currency billing',
      'Properties preferring dedicated onboarding over self-service setup',
      'Multi-property chains needing centralized analytics and rate management',
    ],
  },
  faqItems: [
    { question: 'Can I migrate from Cloudbeds to HotelStack.io?', answer: 'Yes. Our onboarding team handles complete data migration from Cloudbeds including guest profiles, reservation history, and rate plans. Migration is free and typically completed within 48 hours.' },
    { question: 'Does HotelStack.io support the same OTA channels as Cloudbeds?', answer: 'Yes. HotelStack.io connects to 100+ OTAs including Booking.com, Expedia, Airbnb, Agoda, and regional platforms. GDS connectivity via Amadeus and Sabre is also supported.' },
    { question: 'Why is HotelStack.io more affordable than Cloudbeds?', answer: 'HotelStack.io includes all 36 modules in every plan with no add-on fees. Regional pricing in 6 currencies further reduces costs. Cloudbeds charges extra for premium features and offers only USD pricing.' },
    { question: 'Does HotelStack.io work for international properties?', answer: 'Absolutely. HotelStack.io supports multi-currency billing, regional tax compliance, and international payment gateways. Hotels in 40+ countries use HotelStack.io to manage their operations.' },
  ],
};

/* ========================================================================== */
/*  HotelStack.io vs Mews                                                     */
/* ========================================================================== */

const HOTELSTACK_VS_MEWS: ComparisonConfig = {
  slug: 'hotelstack-vs-mews',
  competitorName: 'Mews',
  heroTitle: 'HotelStack.io vs Mews',
  heroSubtitle: 'Mews targets enterprise hotels with premium pricing and paid implementation. HotelStack.io delivers the same modern experience with more modules at a fraction of the cost — free onboarding included.',
  overviewComparison: [
    { label: 'Hotel Segment', hotelstack: 'SMB to Enterprise', competitor: 'Enterprise Only' },
    { label: 'Pricing', hotelstack: 'From $49/mo', competitor: '$200+/mo' },
    { label: 'Total Modules', hotelstack: '36 Built-In', competitor: '15+ Core' },
    { label: 'F&B / POS', hotelstack: 'Built-In', competitor: 'Via Integration' },
    { label: 'UI/UX Quality', hotelstack: 'Modern Dark Luxury', competitor: 'Modern Minimal' },
    { label: 'Onboarding', hotelstack: 'Free + Dedicated', competitor: 'Paid Implementation' },
  ],
  featureTable: [
    {
      category: 'Core PMS',
      features: [
        { name: 'Cloud-Native Architecture', hotelstack: true, competitor: true },
        { name: 'Modern UI/UX', hotelstack: true, competitor: true },
        { name: 'Real-Time Sync', hotelstack: true, competitor: true },
        { name: 'Multi-Device Access', hotelstack: true, competitor: true },
        { name: 'Night Audit Automation', hotelstack: true, competitor: true },
      ],
    },
    {
      category: 'Regional Compliance',
      features: [
        { name: 'Multi-Currency Billing', hotelstack: true, competitor: true },
        { name: 'Regional Tax Automation', hotelstack: true, competitor: 'EU Only' },
        { name: 'Guest Registration Compliance', hotelstack: true, competitor: 'EU Only' },
        { name: 'GDPR Data Protection', hotelstack: true, competitor: true },
        { name: 'Regional Payment Gateways', hotelstack: true, competitor: 'Limited' },
        { name: 'Regional Pricing (6 Currencies)', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Revenue & Distribution',
      features: [
        { name: 'Channel Manager', hotelstack: true, competitor: true },
        { name: 'Dynamic Pricing', hotelstack: true, competitor: true },
        { name: 'Booking Engine', hotelstack: true, competitor: true },
        { name: 'Revenue Analytics', hotelstack: true, competitor: true },
        { name: 'What-If Simulator', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Operations',
      features: [
        { name: 'Housekeeping Management', hotelstack: true, competitor: true },
        { name: 'POS / F&B Module', hotelstack: true, competitor: 'Via Integration' },
        { name: 'Staff Scheduling', hotelstack: true, competitor: false },
        { name: 'Maintenance / Work Orders', hotelstack: true, competitor: false },
        { name: 'Guest Communication', hotelstack: true, competitor: true },
      ],
    },
    {
      category: 'Pricing & Support',
      features: [
        { name: 'Free Onboarding', hotelstack: true, competitor: false },
        { name: 'Free Data Migration', hotelstack: true, competitor: false },
        { name: '24/7 Support', hotelstack: true, competitor: 'Business Hours' },
        { name: 'No Implementation Fee', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Resilience & Offline',
      features: [
        { name: 'Offline-Capable PWA', hotelstack: true, competitor: false },
        { name: 'Offline Mutation Queue', hotelstack: true, competitor: false },
        { name: 'Background Sync on Reconnect', hotelstack: true, competitor: false },
        { name: 'Conflict-Aware Reconciliation', hotelstack: true, competitor: false },
        { name: 'Installable Front Desk', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Corporate & Group Sales',
      features: [
        { name: 'Corporate Rate Management', hotelstack: true, competitor: 'Add-On' },
        { name: 'Group Booking with Rooming Lists', hotelstack: true, competitor: true },
        { name: 'AR Aging & Production Reports', hotelstack: true, competitor: 'Add-On' },
        { name: 'Dining Reservations', hotelstack: true, competitor: 'Third-Party' },
      ],
    },
    {
      category: 'Staff & HR',
      features: [
        { name: 'Staff Directory & Scheduling', hotelstack: true, competitor: 'Third-Party' },
        { name: 'Payroll Processing', hotelstack: true, competitor: false },
        { name: 'Leave & Attendance', hotelstack: true, competitor: false },
        { name: 'Performance Analytics', hotelstack: true, competitor: false },
      ],
    },
  ],
  pricingComparison: {
    hotelstackStarting: '$49',
    competitorStarting: '$200+',
    hotelstackNote: 'All 36 modules included. Free onboarding and migration. Regional pricing in 6 currencies.',
    competitorNote: 'Enterprise pricing ($200+/mo). Paid implementation and onboarding. Premium support extra.',
  },
  advantages: [
    'Approximately 75% lower starting price with more modules included in every plan',
    'Free onboarding and data migration vs paid implementation at Mews',
    'Complete F&B and POS built in vs requiring third-party integration with Mews',
    'Regional pricing in 6 currencies — hotels pay in their local currency',
    'Staff management, payroll, and shift scheduling included',
    'Maintenance and work order management built into the platform',
    '24/7 dedicated support vs business-hours support at Mews',
    'Accessible to SMB hotels, not just enterprise — all plans include all modules',
  ],
  verdict: {
    title: 'Our Verdict',
    summary: 'Mews is an excellent platform for enterprise hotels willing to pay premium pricing. HotelStack.io matches Mews on modern UX and cloud architecture while adding F&B/POS, staff management, maintenance, HR/payroll, and corporate sales — all at roughly one-fourth the price with free onboarding.',
    bestFor: [
      'Hotels wanting Mews-quality UX at a more accessible price point',
      'Properties needing F&B, POS, and operational tools built in',
      'Hotels wanting free onboarding without paid implementation fees',
      'SMB and mid-scale properties priced out of Mews enterprise plans',
      'Multi-property chains needing centralized management with regional compliance',
    ],
  },
  faqItems: [
    { question: 'How does HotelStack.io compare to Mews in terms of UI quality?', answer: 'Both platforms offer modern, intuitive interfaces. HotelStack.io features a dark luxury theme designed specifically for the hospitality industry, with 36 fully integrated modules accessible from one dashboard. Mews has a clean minimal design focused on European workflows.' },
    { question: 'Does Mews include F&B and POS capabilities?', answer: 'Mews requires third-party integrations for F&B and POS. HotelStack.io includes a complete POS terminal, room service module, kitchen display system, and dining management as standard features in every plan.' },
    { question: 'Can I get the same modern experience as Mews at a lower price?', answer: 'Yes. HotelStack.io delivers a modern cloud-native experience with real-time sync, beautiful UI, and 36 modules at $49/month vs Mews at $200+/month. Plus, onboarding and migration are free.' },
    { question: 'Does HotelStack.io offer API integrations like Mews?', answer: 'Yes. HotelStack.io provides an integration marketplace with APIs for payment gateways, OTAs, accounting software, and third-party services. Our API-first architecture supports custom integrations for enterprise requirements.' },
  ],
};

/* ========================================================================== */
/*  Export maps                                                                */
/* ========================================================================== */

export const COMPARISON_CONFIGS: Record<string, LocaleContentMap<ComparisonConfig>> = {
  'hotelstack-vs-hotelogix': { en: injectAI(HOTELSTACK_VS_HOTELOGIX), ...injectAILocaleMap(getP1Locales('hotelstack-vs-hotelogix')) },
  'hotelstack-vs-cloudbeds': { en: injectAI(HOTELSTACK_VS_CLOUDBEDS), ...injectAILocaleMap(getP1Locales('hotelstack-vs-cloudbeds')) },
  'hotelstack-vs-mews': { en: injectAI(HOTELSTACK_VS_MEWS), ...injectAILocaleMap(getP1Locales('hotelstack-vs-mews')) },
  ...Object.fromEntries(Object.entries(COMPARISON_CONFIGS_P2).map(([k, v]) => [k, { en: injectAI(v), ...injectAILocaleMap(getP2Locales(k)) }])),
  ...Object.fromEntries(Object.entries(COMPARISON_CONFIGS_P3).map(([k, v]) => [k, { en: injectAI(v), ...injectAILocaleMap(getP3Locales(k)) }])),
  ...Object.fromEntries(Object.entries(COMPARISON_CONFIGS_P4).map(([k, v]) => [k, { en: v, ...getP4Locales(k) }])),
};

export const COMPARISON_FAQ: Record<string, { question: string; answer: string }[]> = Object.fromEntries(
  Object.entries(COMPARISON_CONFIGS).map(([k, v]) => [k, (v.en as ComparisonConfig).faqItems]),
);

